// src/components/features/auth/SignInForm.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Box,
  Button,
  Typography,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import {
  TOP_APP_BAR_HEIGHT,
  BOTTOM_NAV_BAR_HEIGHT,
} from "../../../constants/constants";
import GoogleIcon from "../../../assets/google-button-icon.png";
import AppleIcon from "../../../assets/apple-button-icon-black.png";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { forgotPassword } from "../../../services/profileService";
import AppBuildFooter from "../../shared/AppBuildFooter";

const SignInForm = ({
  handleEmailPasswordSignIn,
  handleGoogleSignIn,
  handleAppleSignIn,
  loading,
  error,
  registrationClosed = false,
  showGoogle = true,
  showApple = true,
  showEmailPassword = true,
  signupPath = "/signup",
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const navigate = useNavigate();

  const providerNames = [];
  if (showGoogle) providerNames.push("Google");
  if (showApple) providerNames.push("Apple");
  const providersEnabled = providerNames.length > 0;
  const providerHeading = providersEnabled
    ? `Sign in/up with ${providerNames.join(" or ")}`
    : "Sign in";

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please enter your email address first",
      });
      return;
    }
    try {
      await forgotPassword(email?.trim());
      setSnackbar({
        open: true,
        severity: "success",
        message: "Password reset email sent. Please check your inbox.",
      });
    } catch (forgotError) {
      console.error(forgotError);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to send password reset email. Please try again later.",
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleNavigateToSignup = () => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      if (document?.documentElement) document.documentElement.scrollTop = 0;
      if (document?.body) document.body.scrollTop = 0;
      const rootEl = document.getElementById("root");
      if (rootEl && typeof rootEl.scrollTo === "function") {
        rootEl.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    } catch (error) {
      console.warn("[signin] failed to reset scroll before signup navigation:", error);
    }
    navigate(signupPath);
  };

  return (
    <Container
      maxWidth="xs"
      sx={(theme) => ({
        minHeight: `calc(100vh - ${TOP_APP_BAR_HEIGHT}px)`,
        px: 4,
        pt: 4,
        height: "auto",
        color: theme.palette.text.primary,
      })}
    >
      {providersEnabled && (
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: "bold",
              fontSize: "1.3rem",
              textAlign: "left",
              mb: 2,
            }}
          >
            {providerHeading}
          </Typography>
          <Grid container spacing={2}>
            {showGoogle && (
              <Grid item xs={12}>
                <Button
                  onClick={handleGoogleSignIn}
                  variant="contained"
                  fullWidth
                  sx={(theme) => ({
                    border: `1px solid ${theme.colors.blue}`,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : "white",
                    color: theme.palette.text.primary,
                    padding: "10px 40px",
                    fontSize: "1.1rem",
                  })}
                  disabled={loading}
                >
                  <img
                    src={GoogleIcon}
                    alt="Google Icon"
                    style={{
                      width: "35px",
                      position: "absolute",
                      left: "8px",
                    }}
                  />
                  <Box sx={{ flex: 1, textAlign: "center" }}>
                    {loading ? "Signing in..." : "Sign in with Google"}
                  </Box>
                </Button>
              </Grid>
            )}

            {showApple && (
              <Grid item xs={12}>
                <Button
                  onClick={handleAppleSignIn}
                  variant="contained"
                  fullWidth
                  sx={(theme) => ({
                    border: `1px solid ${theme.colors.blue}`,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.background.paper
                        : "white",
                    color: theme.palette.text.primary,
                    padding: "10px 40px",
                    fontSize: "1.1rem",
                  })}
                  disabled={loading}
                >
                  <img
                    src={AppleIcon}
                    alt="Apple Icon"
                    style={{
                      width: "28px",
                      position: "absolute",
                      left: "12px",
                      filter: "none",
                    }}
                  />
                  <Box sx={{ flex: 1, textAlign: "center" }}>
                    {loading ? "Signing In..." : "Sign In with Apple"}
                  </Box>
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {showEmailPassword && (
        <Box sx={{ mt: providersEnabled ? 12 : 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: "bold",
              fontSize: "1.3rem",
              textAlign: "left",
              mb: 2,
            }}
          >
            Or sign in with email
          </Typography>

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                variant="outlined"
                sx={(theme) => ({
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    paddingLeft: "20px",
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.background.paper,
                    "& fieldset": {
                      borderColor: theme.colors.blue,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.colors.blue,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.colors.blue,
                    },
                  },
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        sx={(theme) => ({ color: theme.palette.text.secondary })}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={(theme) => ({
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    paddingLeft: "20px",
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.background.paper,
                    "& fieldset": {
                      borderColor: theme.colors.blue,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.colors.blue,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.colors.blue,
                    },
                  },
                })}
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: "40px" }}>
              <Button
                onClick={() => handleEmailPasswordSignIn(email, password)}
                variant="contained"
                sx={(theme) => ({
                  backgroundColor: theme.colors.blue,
                  color: theme.colors.white,
                  borderRadius: "50px",
                  padding: "14px 40px",
                  marginBottom: "25px",
                  fontWeight: "bold",
                  fontSize: "1rem",
                })}
                disabled={loading}
              >
                {loading ? "Signing In..." : "Sign In"}
              </Button>

              <Typography align="left" sx={{ mb: 2 }}>
                <Link
                  onClick={handleForgotPassword}
                  underline="hover"
                  sx={(theme) => ({
                    cursor: "pointer",
                    color: theme.colors.blue,
                  })}
                >
                  Forgot your password?
                </Link>
              </Typography>

              {!registrationClosed && (
                <Typography align="left">
                  <Link
                    onClick={handleNavigateToSignup}
                    underline="hover"
                    sx={(theme) => ({
                      cursor: "pointer",
                      color: theme.colors.blue,
                    })}
                  >
                    Don't have Google or Apple? Sign up with your email address.
                  </Link>
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      <Box
        sx={{
          mt: 2,
          pb: `calc(${BOTTOM_NAV_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 12px)`,
        }}
      >
        <AppBuildFooter />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SignInForm;
